import { forwardRef, Component as ReactComponent } from 'react';

import { ElementOrElementGetter, isTargetDescendant, Subtract } from '@optimus-web/common';
import { isClickAwayDisabledInTheComponentTree } from './ClickAway/utils';

export interface ScrollAwayOptions {
    roots: ElementOrElementGetter[];
    handler: () => void;
}

export interface ScrollAwayProps {
    registerScrollAway: (options: ScrollAwayOptions) => void;
    deregisterScrollAway: () => void;
    pauseScrollAway: () => void;
    startScrollAway: () => void;
}

const withScrollAway = <P extends ScrollAwayProps, T extends React.Component<P>>(Component: React.ComponentType<P>) => {
    interface OuterRefProps {
        outerRef?: React.Ref<T> | null;
    }

    type OriginalProps = Subtract<P, ScrollAwayProps>;
    type Props = OriginalProps;

    class ScrollAway extends ReactComponent<Props> {
        private registered = false;
        private paused = false;
        private roots?: ElementOrElementGetter[];
        private handler?: () => void;

        componentWillUnmount() {
            if (this.registered) {
                this.deregister();
            }
        }

        register = ({ roots, handler }: ScrollAwayOptions) => {
            if (!this.registered) {
                this.registered = true;
                this.roots = roots;
                this.handler = handler;

                window.addEventListener('scroll', this.handleWindowScroll, true);
            }
        };

        deregister = () => {
            if (this.registered) {
                this.registered = false;
                window.removeEventListener('scroll', this.handleWindowScroll, true);
            }
        };

        pause = () => {
            this.paused = true;
        };

        start = () => {
            this.paused = false;
        };

        handleWindowScroll = (e: Event) => {
            // Check if scroll is from an element that disables click-away (should also disable scroll-away)
            if (isClickAwayDisabledInTheComponentTree(e)) {
                return;
            }

            if (
                !this.paused &&
                this.roots &&
                this.handler &&
                !this.roots.some((r) => isTargetDescendant(e, typeof r === 'function' ? r() : r))
            ) {
                this.handler();
            }
        };

        render() {
            const { outerRef, ...other } = this.props as OuterRefProps;

            const componentProps = {
                ref: outerRef,
                deregisterScrollAway: this.deregister,
                registerScrollAway: this.register,
                pauseScrollAway: this.pause,
                startScrollAway: this.start,
            };

            return <Component {...(other as P)} {...componentProps} />;
        }
    }

    return forwardRef<T, OriginalProps>((props, outerRef) => (
        <ScrollAway {...(props as OriginalProps)} outerRef={outerRef} />
    ));
};

export default withScrollAway;
